/**
 * FollowupSuggestionDialog — shown after finalize (or after email/export
 * on an already-finalized meeting) when Claude detected the participants
 * discussed a follow-up meeting in the source text. Handles two modes:
 *
 *   status='specific' → specific date was mentioned → prefill the invite
 *                        form with that date/title/attendees
 *   status='vague'    → follow-up discussed but no date given → show
 *                        an empty form (date defaults to a week out);
 *                        explain in the header that no date was heard
 *
 * Rules (per product spec):
 *   - User picks attendees from scratch (empty by default). The
 *     originating meeting's attendees are offered as quick-add chips,
 *     but none are preselected.
 *   - Timezone comes from the user's Outlook mailbox settings (handled
 *     by the server — see POST /calendar/followup-invite). The dialog
 *     just shows a local-wallclock preview.
 *
 * On confirm we POST /calendar/followup-invite and surface the webLink
 * so the user can jump to the new event in Outlook if they want.
 */
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import { Modal, ModalHeader } from '../ui/Modal.tsx';
import { GraphErrorBanner } from '../GraphErrorBanner.tsx';

export interface FollowupDetection {
  /** 'specific' → date was mentioned; 'vague' → follow-up discussed
   *  without a date. The dialog handles both; 'none' never reaches here. */
  status: 'specific' | 'vague';
  proposedDate?: string; // ISO datetime hint — only present when status=specific
  proposedTitle?: string;
  proposedAttendees?: string[];
  evidenceQuote?: string;
}

interface AttendeeOption {
  name: string;
  email: string;
  isOjb: boolean;
}

interface Props {
  isOpen: boolean;
  detection: FollowupDetection | null;
  sourceMeetingId: string;
  sourceMeetingTitle: string;
  /** Attendees on the source meeting — shown as quick-add chips. */
  availableAttendees: AttendeeOption[];
  onClose: () => void;
  /** Called after the Graph event is created; receives the webLink. */
  onCreated?: (webLink: string | null) => void;
}

export function FollowupSuggestionDialog({
  isOpen,
  detection,
  sourceMeetingId,
  sourceMeetingTitle,
  availableAttendees,
  onClose,
  onCreated,
}: Props) {
  // Local editable fields — seeded from the detection but user-overridable.
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(''); // YYYY-MM-DD
  const [time, setTime] = useState('10:00'); // HH:mm (24h)
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [location, setLocation] = useState('');
  const [isOnlineMeeting, setIsOnlineMeeting] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [manualEmails, setManualEmails] = useState('');
  const [createdWebLink, setCreatedWebLink] = useState<string | null>(null);

  // Reset on open so a previous session's state doesn't leak in.
  useEffect(() => {
    if (!isOpen || !detection) return;
    setTitle(
      detection.proposedTitle?.trim() ||
        `${sourceMeetingTitle} - Follow-up`,
    );
    const seed = parseProposedDate(detection.proposedDate);
    setDate(seed.date);
    setTime(seed.time);
    setDurationMinutes(60);
    setLocation('');
    setIsOnlineMeeting(false);
    // User picks attendees from scratch — start empty per product rule.
    setSelectedEmails([]);
    setManualEmails('');
    setCreatedWebLink(null);
  }, [isOpen, detection, sourceMeetingTitle]);

  const createInvite = useMutation({
    mutationFn: async () => {
      const emails = uniqueEmails([
        ...selectedEmails,
        ...splitEmails(manualEmails),
      ]);
      const startLocal = `${date}T${time.padEnd(5, '0')}:00`;

      const body = [
        detection?.evidenceQuote
          ? `Per "${sourceMeetingTitle}": "${detection.evidenceQuote}"`
          : `Follow-up to "${sourceMeetingTitle}".`,
        '',
        'Created from OJB Meeting Minutes.',
      ].join('\n');

      // Browser-supplied IANA timezone — used by the api as a
      // tertiary fallback when Graph's mailboxSettings call
      // fails AND the user has no cached tz yet (typically a
      // first-time invite right after a Conditional Access
      // policy blocks the MailboxSettings.Read scope). See
      // services/timezoneResolver.ts for the full chain.
      const clientTimezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const url =
        '/calendar/followup-invite' +
        (clientTimezone
          ? `?clientTimezone=${encodeURIComponent(clientTimezone)}`
          : '');

      return api.post<{ id: string; webLink: string | null; timeZone: string }>(
        url,
        {
          startLocal,
          durationMinutes,
          subject: title,
          body,
          location: location.trim() || undefined,
          attendees: emails.map((e) => ({ email: e })),
          isOnlineMeeting,
          sourceMeetingId,
        },
      );
    },
    onSuccess: (res) => {
      setCreatedWebLink(res.webLink);
      onCreated?.(res.webLink);
    },
  });

  // Friendly classification of the api's TIMEZONE_UNAVAILABLE
  // 503 — surfaces a "couldn't determine your timezone" banner
  // with a Retry button instead of a raw error message. Other
  // errors fall through to the generic error display below.
  const tzUnavailable =
    createInvite.error instanceof ApiError &&
    createInvite.error.status === 503 &&
    typeof createInvite.error.data === 'object' &&
    createInvite.error.data !== null &&
    'code' in createInvite.error.data &&
    (createInvite.error.data as { code?: string }).code === 'TIMEZONE_UNAVAILABLE';

  if (!isOpen || !detection) return null;

  const canSubmit = !!date && !!time && title.trim().length > 0 && !createInvite.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      ariaLabel="Schedule follow-up meeting"
    >
      <ModalHeader
        title="Schedule follow-up meeting?"
        subtitle={
          detection.status === 'specific'
            ? "A specific follow-up date was mentioned in this meeting. Review the details below - we'll create a real Outlook invite in your calendar."
            : "The group agreed to meet again, but no date was mentioned. Pick a date and time below and we'll create a real Outlook invite in your calendar."
        }
        onClose={onClose}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        {createdWebLink !== null ? (
          <div className="space-y-4 px-5 py-6 text-sm">
            <div className="rounded border border-pine/40 bg-pine/5 px-4 py-3 text-charcoal">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-pine">
                Invite created
              </div>
              <p>
                The follow-up is now on your Outlook calendar. Attendees
                will receive the invite as soon as Outlook syncs.
              </p>
              {createdWebLink && (
                <a
                  href={createdWebLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-xs font-medium text-pine underline hover:text-pine/80"
                >
                  Open in Outlook →
                </a>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-pine px-4 py-2 text-sm font-medium text-cloud hover:bg-pine/90"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 px-5 py-5 text-sm">
              {detection.evidenceQuote && (
                <div className="rounded border border-smoke/40 bg-cloud/40 px-3 py-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60">
                    Detected from the minutes
                  </div>
                  <div className="mt-1 italic text-charcoal/80">
                    &ldquo;{detection.evidenceQuote}&rdquo;
                  </div>
                </div>
              )}

              <label className="block">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-pine">
                  Title
                </span>
                <input
                  {...NO_AUTOFILL}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded border border-smoke/40 bg-white px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none"
                />
              </label>

              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-pine">
                    Date
                  </span>
                  <input
                    {...NO_AUTOFILL}
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full rounded border border-smoke/40 bg-white px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-pine">
                    Start
                  </span>
                  <input
                    {...NO_AUTOFILL}
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full rounded border border-smoke/40 bg-white px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-pine">
                    Duration
                  </span>
                  <select
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(Number(e.target.value))}
                    className="w-full rounded border border-smoke/40 bg-white px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none"
                  >
                    <option value={30}>30 min</option>
                    <option value={45}>45 min</option>
                    <option value={60}>1 hr</option>
                    <option value={90}>1.5 hr</option>
                    <option value={120}>2 hr</option>
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-pine">
                  Location (optional)
                </span>
                <input
                  {...NO_AUTOFILL}
                  type="text"
                  name="followup-venue"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Conference Room A, or leave blank for Teams"
                  className="w-full rounded border border-smoke/40 bg-white px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none"
                />
              </label>

              <label className="flex items-center gap-2 text-sm text-charcoal">
                <input
                  type="checkbox"
                  checked={isOnlineMeeting}
                  onChange={(e) => setIsOnlineMeeting(e.target.checked)}
                  className="h-4 w-4 accent-pine"
                />
                Add Teams meeting link
              </label>

              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-pine">
                  Attendees
                </div>
                <p className="mb-2 text-[11px] text-smoke">
                  Pick from the original meeting or add emails manually. No
                  one is invited by default - you choose who&rsquo;s in.
                </p>

                {availableAttendees.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {availableAttendees.map((a) => {
                      const on = selectedEmails.includes(a.email);
                      return (
                        <button
                          key={a.email}
                          type="button"
                          onClick={() =>
                            setSelectedEmails((prev) =>
                              on
                                ? prev.filter((e) => e !== a.email)
                                : [...prev, a.email],
                            )
                          }
                          className={
                            'rounded-full border px-3 py-1 text-xs transition-colors ' +
                            (on
                              ? 'border-pine bg-pine text-cloud'
                              : 'border-smoke/40 bg-white text-charcoal hover:border-pine hover:text-pine')
                          }
                        >
                          {a.name || a.email}
                          {a.isOjb && <span className="ml-1 opacity-70">· OJB</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                <input
                  {...NO_AUTOFILL}
                  type="text"
                  value={manualEmails}
                  onChange={(e) => setManualEmails(e.target.value)}
                  placeholder="Add emails, comma-separated"
                  className="w-full rounded border border-smoke/40 bg-white px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none"
                />
              </div>

              {/* Specific tz-unavailable case keeps its own banner
                  (terminology is more specific than the generic
                  Graph error classifier). All other Graph errors
                  (reauth / scope_blocked / throttled / outage / etc.)
                  go through GraphErrorBanner so the user sees a
                  classified, actionable message instead of a raw
                  upstream string. */}
              {tzUnavailable && (
                <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <div className="font-semibold">
                    Couldn&rsquo;t determine your timezone
                  </div>
                  <p className="mt-0.5">
                    Outlook didn&rsquo;t return your mailbox timezone, so we
                    can&rsquo;t safely schedule this invite at the right
                    wallclock. Try again - if it keeps failing, sign out and
                    sign back in to refresh permissions, or contact OJB IT
                    if MailboxSettings.Read has been blocked.
                  </p>
                </div>
              )}
              {createInvite.error && !tzUnavailable && (
                <GraphErrorBanner err={createInvite.error} />
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-smoke/30 px-5 py-4">
              <button
                type="button"
                onClick={onClose}
                disabled={createInvite.isPending}
                className="rounded border border-smoke/40 px-4 py-2 text-sm text-charcoal transition-colors hover:border-smoke hover:text-charcoal disabled:opacity-50"
              >
                Not now
              </button>
              <button
                type="button"
                disabled={!canSubmit}
                onClick={() => createInvite.mutate()}
                className="rounded bg-pine px-4 py-2 text-sm font-medium text-cloud transition-colors hover:bg-pine/90 disabled:cursor-not-allowed disabled:bg-smoke/50"
              >
                {createInvite.isPending ? 'Creating…' : 'Create Outlook invite'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * Parse a proposed-date string from Claude into (YYYY-MM-DD, HH:mm).
 * Claude is instructed to emit ISO-like datetimes ("2026-05-03T10:00:00"
 * or bare dates "2026-05-03"), but we defensively accept either and
 * default the time to 10:00 when missing.
 */
function parseProposedDate(raw: string | undefined): { date: string; time: string } {
  if (!raw) {
    // Default: a week from today at 10:00 AM.
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return { date: d.toISOString().slice(0, 10), time: '10:00' };
  }
  const trimmed = raw.trim();
  // Date-only
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return { date: trimmed, time: '10:00' };
  }
  // Datetime with optional T separator
  const m = trimmed.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (m) return { date: m[1], time: m[2] };
  // Fallback: parse via Date and extract.
  const d = new Date(trimmed);
  if (!Number.isNaN(d.getTime())) {
    const iso = d.toISOString();
    return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
  }
  return { date: '', time: '10:00' };
}

function splitEmails(s: string): string[] {
  return s
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0 && x.includes('@'));
}

function uniqueEmails(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of list) {
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}
